import { createReducer, createSelector } from '@reduxjs/toolkit'
import { keyBy } from 'lodash'

import { PermissionValue } from 'common/system-permissions'

import types from './types'

export { default as actions } from './actions'

export const EmailFrequency = {
  Off: 'off',
  Immediately: 'immediately',
  Daily: 'daily',
  Weekly: 'weekly',
} as const

export type EmailFrequencyType = (typeof EmailFrequency)[keyof typeof EmailFrequency]
export type CadenceTypes = Exclude<EmailFrequencyType, typeof EmailFrequency.Off>
export const TargetHighSchoolVisitCreation = 'TargetHighSchoolVisitCreation' as const

export type User = {
  Id: string
  Scid?: number
  FirstName: string
  MiddleName: string
  LastName: string
  Email: string
  IsShadow: boolean
  Permissions?: PermissionValue[]
  Roles?: string[]
  Preferences?: {
    UseHighSchoolExplorer?: boolean
  }
  AvatarUrl: string
  Title?: string
  Phone?: string
  PhoneExtension?: string
  CellPhone?: string
  LinkedInURL?: string
  NotificationPreference?: {
    EmailOptIn?: boolean
    HandraisersEmailOptIn?: boolean
    Visits?: {
      SourceFrequencies?: Record<string, EmailFrequencyType>
    }
  }
}
type UserState = {
  record?: User
}

type StoreState = {
  user: UserState
  features?: {
    record?: {
      [key: string]: boolean
    }
  }
}

const initialState: UserState = {}

function throwIfNotSet<T extends keyof User>(record: User | undefined, property: T) {
  const val = record?.[property]

  if (val === undefined) {
    throw new Error(`${property} is undefined on user record in redux store`)
  }

  return val
}

export const selectUser = (state: StoreState) => state.user.record
const selectPermissions = (state: StoreState) => state.user.record?.Permissions || []
const selectKeyedPermissions = createSelector(selectPermissions, (perms) => keyBy(perms))
export const selectEmail = (state: StoreState) => throwIfNotSet(state.user.record, 'Email')
export const selectUserId = (state: StoreState) => throwIfNotSet(state.user.record, 'Id')
export const selectUserFirstName = (state: StoreState) => state.user.record?.FirstName || ''
export const selectUserLastName = (state: StoreState) => state.user.record?.LastName || ''
export const selectUserScid = (state: StoreState) => throwIfNotSet(state.user.record, 'Scid')
export const hasPermission = (state: StoreState, permission?: PermissionValue) => {
  if (!permission) {
    throw new Error(`invalid permission "${permission}"`)
  }
  return !!selectKeyedPermissions(state)[permission]
}

export const isShadow = (state: StoreState) => state.user?.record?.IsShadow || false

export const isHighSchoolExplorerEnabled = (state: StoreState) => {
  if (state.features?.record?.HighSchoolExplorerPremiumPreview) {
    return true
  }
  return state.user?.record?.Preferences?.UseHighSchoolExplorer || false
}

export default createReducer(initialState, {
  [types.FETCH_USER_SUCCESS]: (state, action) => {
    state.record = action.payload
  },
})
